// The data sources (phase 4 §1): GET /sources — their names, what they can
// do, whether they are connected or paused, and the attribution each asks
// for. Read once per page load, shared by every component. Known by name
// before it answers, so a first render never shows a bare id.
import { useEffect, useState } from 'react';
import api from './config/api';

export const SOURCE_LABELS = { tmdb: 'TMDB', openlibrary: 'Open Library', bnf: 'BnF', googlebooks: 'Google Books', igdb: 'IGDB' };

/** The identifier scheme each source's ids are kept under in an item (Source::scheme()). */
export const SOURCE_SCHEMES = { tmdb: 'tmdb', openlibrary: 'olid', bnf: 'bnf', googlebooks: 'gbooks', igdb: 'igdb' };

/** A source's name: from GET /sources once it answered, else the shipped one, else its id. */
export const sourceLabel = (id, sources, lang = 'en') => {
  const found = (sources || []).find(source => source.id === id);
  return (found && found.labels && (found.labels[lang] || found.labels.en)) || SOURCE_LABELS[id] || id;
};

let loaded = null;
let pending = null;
const load = () => {
  if (!pending) {
    pending = Promise.resolve().then(() => api.get('/sources')).then((response) => {
      loaded = Array.isArray(response.data) ? response.data : [];
      return loaded;
    }).catch((error) => {
      pending = null;
      throw error;
    });
  }
  return pending;
};

/** Forgets what GET /sources said (tests; a source paused or connected since). */
export const forgetSources = () => {
  loaded = null;
  pending = null;
};

/** The sources ([] while loading). */
export function useSources() {
  const [sources, setSources] = useState(loaded || []);
  useEffect(() => {
    let alive = true;
    load().then((data) => { if (alive) setSources(data); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  return sources;
}

/** The attribution of a source in the UI language: {text, url} (null when unknown). */
export const attributionOf = (id, sources, lang) => {
  const found = (sources || []).find(source => source.id === id);
  if (!found || !found.attribution) return null;
  const text = found.attribution.text || {};
  return { text: text[lang] || text.en || sourceLabel(id, sources, lang), url: found.attribution.url };
};
